import { Tab } from '@headlessui/react';
import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import Button from '@/components/Button';
import { removeInlineFontFamily } from '@/lib/common';
import useIsMobile from '@/lib/useIsMobile';
import ShareItem from '@/modules/blog/ShareItem';
import { JDEmptyContent } from './JDEmptyContent';
import type { JobPageType } from './JDPage';
import JobApplicationForm from './JobApplicationForm';
import ReferalForm from './ReferalForm';

type Props = {
  pageType: JobPageType;
  jdContent?: string;
  jDDescription?: string;
  jDJobRequirement?: string;
  jDWhyLoveWorkingHere?: string;
  jobTitle: string;
  jobID: number;
  jobSlug: string;
  benefit?: string;
};

export default function JDMainContent({
  pageType,
  jdContent,
  jobTitle,
  jobID,
  jobSlug,
  jDDescription,
  jDJobRequirement,
  jDWhyLoveWorkingHere,
  benefit,
}: Props) {
  const isMobile = useIsMobile();
  const { query, replace, locale } = useRouter();

  const defaultIndex = useMemo(() => {
    switch (pageType) {
      case 'referral':
        return 2;

      case 'apply':
        return 1;
      default:
        return 0;
    }
  }, [pageType]);

  return (
    <section className="desktop:py-10 min-h-content bg-white py-6">
      <div className="desktop:px-8 mx-auto flex w-full flex-col px-4">
        <Tab.Group
          defaultIndex={defaultIndex}
          onChange={(index) => {
            replace({
              pathname: index === 1 ? '/job/[slug]/apply' : '/job/[slug]',
              query: { slug: query.slug },
            });
          }}
        >
          <Tab.List className="-mx-4 flex flex-row">
            <Link
              href={{
                pathname: '/job/[slug]',
                query: { slug: query.slug },
              }}
              passHref
              locale={locale}
              legacyBehavior
            >
              <Tab
                as="a"
                className={({ selected }) =>
                  cn(
                    'text-subtitle18 h-full px-4 py-2 text-justify font-semibold focus-visible:outline-none',
                    selected
                      ? 'text-primary-50 border-primary-50 whitespace-nowrap border-b-2 font-bold'
                      : 'hover:text-neutral-90 truncate text-neutral-50'
                  )
                }
              >
                Công việc
              </Tab>
            </Link>
            <Link
              href={{
                pathname: '/job/[slug]/apply',
                query: { slug: query.slug },
              }}
              passHref
              locale={locale}
              legacyBehavior
            >
              <Tab
                as="a"
                className={({ selected }) =>
                  cn(
                    'text-subtitle18 px-4 py-2 text-justify font-semibold focus-visible:outline-none',
                    selected
                      ? 'text-primary-50 border-primary-50 whitespace-nowrap border-b-2 font-bold'
                      : 'hover:text-neutral-90 truncate text-neutral-50'
                  )
                }
              >
                Ứng tuyển
              </Tab>
            </Link>
            <Link
              href={{
                pathname: '/job/[slug]/referal',
                query: { slug: query.slug },
              }}
              passHref
              locale={locale}
              legacyBehavior
            >
              <Tab
                as="a"
                className={({ selected }) =>
                  cn(
                    'text-subtitle18 h-full px-4 py-2 text-justify font-semibold focus-visible:outline-none',
                    selected
                      ? 'text-primary-50 border-primary-50 whitespace-nowrap border-b-2 font-bold'
                      : 'hover:text-neutral-90 truncate text-neutral-50'
                  )
                }
              >
                Giới thiệu ứng viên
              </Tab>
            </Link>
          </Tab.List>
          <hr className="text-neutral-15 h-2" />
          <Tab.Panels className="mt-10">
            <Tab.Panel className="desktop:space-x-6 flex w-full flex-row flex-nowrap space-x-4">
              {!isMobile && (
                <aside>
                  <ShareItem
                    className="sticky top-1/3 left-20 z-10 mt-4"
                    title={jobTitle}
                    slug={jobSlug}
                    job
                  />
                </aside>
              )}
              <div className="flex w-full flex-col space-y-6">
                <div className="flex flex-col gap-3">
                  {jDJobRequirement ||
                  jDDescription ||
                  jDWhyLoveWorkingHere ||
                  benefit ? (
                    <>
                      {jDDescription && (
                        <div className="flex flex-col gap-2">
                          <h3 className="text-body16 text-neutral-90 desktop:text-subtitle20 text-left font-semibold">
                            Job description
                          </h3>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: removeInlineFontFamily(jDDescription),
                            }}
                            className="prose"
                          />
                        </div>
                      )}
                      {jDJobRequirement && (
                        <div className="flex flex-col gap-2">
                          <h3 className="text-body16 text-neutral-90 desktop:text-subtitle20 text-left font-semibold">
                            Job requirement
                          </h3>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: removeInlineFontFamily(jDJobRequirement),
                            }}
                            className="prose"
                          />
                        </div>
                      )}
                      {jDWhyLoveWorkingHere && (
                        <div className="flex flex-col gap-2">
                          <h3 className="text-body16 text-neutral-90 desktop:text-subtitle20 text-left font-semibold">
                            Why you'll love working here
                          </h3>
                          <div
                            dangerouslySetInnerHTML={{
                              __html:
                                removeInlineFontFamily(jDWhyLoveWorkingHere),
                            }}
                            className="prose"
                          />
                        </div>
                      )}
                      {benefit && (
                        <div className="flex flex-col gap-2">
                          <h3 className="text-body16 text-neutral-90 desktop:text-subtitle20 text-left font-semibold">
                            Benefit
                          </h3>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: removeInlineFontFamily(benefit),
                            }}
                            className="prose"
                          />
                        </div>
                      )}
                      <div>
                        <Button
                          type="link"
                          href={{
                            pathname: '/job/[slug]/apply',
                            query: { slug: query.slug },
                          }}
                          className="desktop:w-auto w-full"
                        >
                          Ứng tuyển ngay
                        </Button>
                      </div>
                    </>
                  ) : jdContent ? (
                    <>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: removeInlineFontFamily(jdContent),
                        }}
                        className="prose"
                      />
                      <div>
                        <Button
                          type="link"
                          href={{
                            pathname: '/job/[slug]/apply',
                            query: { slug: query.slug },
                          }}
                          className="desktop:w-auto w-full"
                        >
                          Ứng tuyển ngay
                        </Button>
                      </div>
                    </>
                  ) : (
                    <JDEmptyContent />
                  )}
                </div>
                {isMobile && (
                  <ShareItem
                    className="mb-4 !flex-row !justify-start space-y-0 space-x-4"
                    title={jobTitle}
                    slug={jobSlug}
                    job
                  />
                )}
              </div>
            </Tab.Panel>
            <Tab.Panel className="desktop:min-w-3xl w-full">
              <JobApplicationForm jobTitle={jobTitle} jobID={jobID} />
            </Tab.Panel>
            <Tab.Panel className="desktop:min-w-3xl w-full">
              <ReferalForm jobTitle={jobTitle} jobID={jobID} />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </section>
  );
}
